/**
 * Shared state untuk Ask-AI bar.
 *
 * Sebelumnya state hidup di local React useState di AskAiBar — fine ketika
 * cuma 1 mount point. Sekarang kita render 2 instance (1 di kanan untuk
 * desktop xl+, 1 di bawah untuk mobile <xl) supaya AI accessible di semua
 * viewport — sehingga state harus di-lift.
 *
 * Pattern: same lightweight subscriber store sama kayak tryIt.ts —
 * useSyncExternalStore + module-level mutable state. No redux/zustand
 * dependency dibutuhkan, gak ada hot-reload re-init issue.
 */

import { useSyncExternalStore } from 'react';
import type { AiMessage, StreamErrorInfo } from '@/lib/aiStream';

export interface AskAiState {
  input: string;
  messages: AiMessage[];
  streamError: StreamErrorInfo | null;
  streaming: boolean;
}

const initial: AskAiState = {
  input: '',
  messages: [],
  streamError: null,
  streaming: false,
};

let state: AskAiState = initial;
const listeners = new Set<() => void>();
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const snapshot = () => state;
const emit = () => {
  for (const l of listeners) l();
};

export function setInput(input: string) {
  state = { ...state, input };
  emit();
}

export function setMessages(messages: AiMessage[]) {
  state = { ...state, messages };
  emit();
}

export function appendMessage(msg: AiMessage) {
  state = { ...state, messages: [...state.messages, msg] };
  emit();
}

/** Replace the last message (typical for streaming the assistant turn). */
export function replaceLastMessage(msg: AiMessage) {
  state = {
    ...state,
    messages: state.messages.length > 0
      ? [...state.messages.slice(0, -1), msg]
      : [msg],
  };
  emit();
}

/** Pop the last message (used pas error fire — gak nyimpan placeholder kosong). */
export function popLastMessage() {
  state = { ...state, messages: state.messages.slice(0, -1) };
  emit();
}

export function setStreamError(streamError: StreamErrorInfo | null) {
  state = { ...state, streamError };
  emit();
}

export function setStreaming(streaming: boolean) {
  state = { ...state, streaming };
  emit();
}

/**
 * Module-level abort handle untuk current streaming session. Cuma satu
 * session aktif sekaligus (guarded oleh `streaming` flag), jadi safe pakai
 * single ref. Stop button di mana pun mount-nya akan abort fetch yg sama.
 */
let abortController: AbortController | null = null;

export function setAbortController(ac: AbortController | null) {
  abortController = ac;
}

export function abortCurrentStream() {
  abortController?.abort();
  abortController = null;
}

export function useAskAi(): AskAiState {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
